<template>
  <BaseMenu
    v-if="users"
    :items="usersList"
    :title="$t('search-filters.assignees')"
    :selection="usersSelected"
    :attach="attach"
    :floating="floating"
    @add-item="addItemToSelection"
    @remove-item="removeItemFromSelection"
    @clear="clearAssignation"
    @click-outside="applyImmediately"
    isMultiple
    searchAllowed
    ref="baseAssignationMenu"
  >
    <template slot="activator">
      <div
        v-if="value.length < 1"
        class="base-assignation__add"
        role="button"
        tabindex="0"
        @keydown.enter.prevent="$event.target.click()"
        @keydown.space.prevent="$event.target.click()"
      >
        <BaseIcon name="users" />
        {{ assignText }}
      </div>
      <div v-else-if="value.length <= 3" class="base-assignation__avatars">
        <BaseAvatar
          v-for="(assignee, index) in value"
          :key="index"
          :name="getFullName(assignee.firstName, assignee.lastName)"
          v-tooltip="{ content: getFullName(assignee.firstName, assignee.lastName), placement: tooltipPlacement }"
          interactive
          :color="isCurrentUser(assignee) ? '#fcdbd9' : '#d9e5f9'"
        />
      </div>
      <div v-else class="base-assignation__avatars">
        <BaseAvatar
          v-for="(assignee, index) in value.slice(0, 3)"
          :key="index"
          :name="getFullName(assignee.firstName, assignee.lastName)"
          v-tooltip="{ content: getFullName(assignee.firstName, assignee.lastName), placement: tooltipPlacement }"
          interactive
          :color="isCurrentUser(assignee) ? '#fcdbd9' : '#d9e5f9'"
        />
        <v-popover placement="top" trigger="hover" popoverClass="tender-bottom-nav__nav-tooltip">
          <div class="base-assignation__more-people">+{{ value.slice(-(value.length - 3)).length }}</div>
          <template v-slot:popover>
            <div v-for="(assignee, index) in value.slice(-(value.length - 3))" :key="index">
              {{ getFullName(assignee.firstName, assignee.lastName) }}
            </div>
          </template>
        </v-popover>
      </div>
    </template>
    <template v-slot:default="{ item }">
      <div class="items">
        <div class="items__title">{{ item.name }}</div>
        <div class="items__subtitle">{{ item.email }}</div>
      </div>
    </template>
  </BaseMenu>
</template>

<script>
/*

Locally, we manipulate <Assignee> object as for the variable innValue and this is also what received from server,
but <BaseMenu> is only working with usersList and <id> (and not userId), so that's why we're doing some manipulations.

*/

import BaseAvatar from '@/components/base/BaseAvatar';
import BaseIcon from '@/components/base/BaseIcon';
import BaseMenu from '@/components/base/BaseMenu';
import DebounceMixin from '@/mixins/debounce-mixin';

import { VPopover } from 'v-tooltip';
import { assignableUsers } from '@/util/user-util';
import { mapGetters } from 'vuex';

export default {
  name: 'BaseAssignation',
  components: {
    BaseAvatar,
    BaseIcon,
    BaseMenu,
    VPopover,
  },
  mixins: [DebounceMixin],
  props: {
    value: {
      type: Array,
    },
    tooltipPlacement: {
      type: String,
      required: false,
      default: 'top',
    },
    attach: {
      type: String,
    },
    // See BaseMenu: the menu escapes a clipping container instead of attaching.
    floating: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      innerValue: [],
      isDirty: false,
    };
  },
  computed: {
    ...mapGetters('tender', ['tender']),
    ...mapGetters('auth', ['currentUser']),
    ...mapGetters('tenant', ['roleUserOnly', 'users']),
    assignText() {
      return this.$t('page.tenders.assignation.assign-to');
    },
    usersList() {
      return assignableUsers(this.currentUser, this.users, this.roleUserOnly)
        .map(({ id, email, fullName }) => ({
          id,
          email,
          name: fullName,
        }))
        .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    },
    usersSelected() {
      return this.innerValue.map(({ userId, ...rest }) => ({
        id: userId,
        ...rest,
      }));
    },
  },
  methods: {
    mapToAssignee(user) {
      return {
        userId: user.id,
        email: user.email,
        firstName: user.firstName,
        lastName: user.lastName,
        assignedDate: null,
      };
    },
    isCurrentUser(assignee) {
      return assignee.userId === this.currentUser.id;
    },
    getFullName(firstName, lastName) {
      return firstName + ' ' + lastName;
    },
    addItemToSelection(item) {
      this.isDirty = true;
      const user = this.users.find((user) => user.id === item.id);
      const assignee = this.mapToAssignee({ ...user });
      this.innerValue.push(assignee);
      this.updateAssignees();
    },
    removeItemFromSelection(item) {
      this.isDirty = true;
      this.innerValue = this.innerValue.filter((assignee) => assignee.userId !== item.id);
      this.updateAssignees();
    },
    clearAssignation() {
      this.isDirty = true;
      if (this.innerValue.length !== 0) this.innerValue = [];
      this.applyChanges();
      this.closeMenu();
    },
    updateAssignees() {
      this.debounce(() => {
        this.applyChanges();
        // A phone's sheet stays open for the next pick, until "Terminé".
        if (!this.$refs.baseAssignationMenu.inSheet) this.closeMenu();
      }, 2500);
    },
    applyImmediately() {
      if (!this.isDirty) return;
      this.debounce(null, 0, true);
      this.applyChanges();
    },
    applyChanges() {
      const userIds = this.innerValue.map((user) => user.userId);
      this.$emit('change', userIds);
      this.$emit('change-users', this.innerValue);
      this.isDirty = false;
    },
    closeMenu() {
      this.isDirty = false;
      this.$refs.baseAssignationMenu.menu = false;
    },
    // Drops the unsaved local list and shows `value` again — for a caller whose
    // save failed (the prop did not change, so the watcher cannot do it).
    reset() {
      this.innerValue = [...this.value];
      this.isDirty = false;
    },
  },
  watch: {
    value() {
      this.innerValue = [...this.value];
    },
  },
  created() {
    this.innerValue = [...this.value];
  },
  beforeDestroy() {
    this.innerValue = [];
  },
};
</script>

<style lang="scss">
.base-assignation {
  &__add {
    @include inline-add-button;
    gap: 6px;

    .base-icon {
      width: 14px;
      height: 14px;
      flex-shrink: 0;
    }
  }

  &__avatars {
    display: flex;
    align-items: center;
    justify-content: flex-start;

    .base-avatar {
      margin-right: -8px;
    }

    .trigger {
      color: $color-bluey-gray;
      padding-left: 16px;
      display: flex !important;
    }
  }

  &__more-people {
    width: 20px;
  }
}

.items {
  display: flex;
  flex-direction: column;
  gap: 4px;

  &__title {
    font-size: 14px;
    color: $color-normal;
    max-width: 250px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__subtitle {
    font-size: 12px;
    color: $color-neutral;
    max-width: 250px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}
</style>
